import React, { useState } from 'react';
import Icon from './Icon';

/**
 * What the agent actually did, rendered from the server's tool log.
 *
 * This replaces a <details> containing JSON.stringify(result, null, 2). That
 * presentation only looked deliberate because it was set in a monospace face:
 * the moment the typewriter font went, it read as a stack trace. The data was
 * always structured — tool name, arguments, result — it simply was not being
 * designed.
 *
 * Two states:
 *
 *   busy      an optimistic sequence, so the wait has a shape. The server
 *             returns its tool log only when the whole request finishes, so
 *             these are placeholders, not live events. Real streaming needs
 *             the endpoint to send progress — until then this is honest about
 *             being a guess by never claiming a duration.
 *
 *   resolved  the real log. One row per call, expandable to the record.
 */

const PENDING_SEQUENCE = [
  'Reading the creative library',
  'Loading the governance rules',
  'Checking the request against them',
];

const TOOL_LABEL = {};

const BUILD_TOOLS = ['create_campaign', 'create_search_campaign'];

/**
 * What a build step actually did, from its result.
 *
 * It used to say "Sent the campaign to Meta" whatever happened - including
 * when governance blocked it and nothing reached Meta at all. On a governance
 * product the step list must not claim a send that did not happen.
 */
function buildLabel(call) {
  const r = call.result || {};
  const where = r.platform === 'google' || call.tool === 'create_search_campaign'
    ? 'Google Ads' : 'Meta';
  if (r.outcome === 'invalid') return 'Sent back to fix — nothing checked or created';
  if (r.outcome === 'BLOCK') return `Blocked by a rule — nothing sent to ${where}`;
  if (r.outcome === 'FLAG') return `Held for review — nothing sent to ${where}`;
  if (r.outcome === 'APPROVED' && r.created) return `Approved and created on ${where}, paused`;
  if (r.outcome === 'APPROVED' && r.created === false) return `Approved, but ${where} refused it`;
  if (r.error) return 'Could not complete this step';
  return `Checked against the rules`;
}

function labelFor(call) {
  if (BUILD_TOOLS.includes(call.tool)) return buildLabel(call);
  return TOOL_LABEL[call.tool] || prettyTool(call.tool);
}

/** get_creative_library -> "Get creative library" */
function prettyTool(name) {
  const words = String(name).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function prettyKey(key) {
  const words = String(key).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatValue(value) {
  if (value === true) return { text: 'yes', tone: 'yes' };
  if (value === false) return { text: 'no', tone: 'no' };
  if (value === null || value === undefined) return { text: '—', tone: null };
  if (Array.isArray(value)) return { text: value.join(', ') || '—', tone: null };
  if (typeof value === 'object') {
    const pairs = Object.entries(value).map(([k, v]) => `${k}: ${v}`);
    return { text: pairs.join(' · ') || '—', tone: null };
  }
  if (typeof value === 'number') return { text: value.toLocaleString('en-GB'), tone: null };
  return { text: String(value), tone: null };
}

function RecordList({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) {
    return <dl className="pl-kv"><dt>Nothing recorded</dt><dd /></dl>;
  }
  return (
    <dl className="pl-kv">
      {entries.map(([key, value]) => {
        const { text, tone } = formatValue(value);
        return (
          <React.Fragment key={key}>
            <dt>{prettyKey(key)}</dt>
            <dd className={tone || undefined}>{text}</dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}

function Step({ call }) {
  const [open, setOpen] = useState(false);
  // A tool that returned a block is a failure in the sequence, not a success
  // with bad news inside it. The dot has to say so.
  const failed = call.result && (call.result.outcome === 'BLOCK' || call.result.error
    || call.result.created === false);

  return (
    <>
      <div className={`step ${failed ? 'fail' : 'done'} ${open ? 'expanded' : ''}`}>
        <span className="step-label">{labelFor(call)}</span>
        <button
          type="button"
          className="step-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the record for ${call.tool}`}
        >
          <Icon name="down" size={13} strokeWidth={2.4} />
        </button>
      </div>

      {open && (
        <div className="step-payload">
          <div className="pl-h">
            Called <span className="t">{call.tool}</span>
          </div>
          <RecordList data={call.args} />
          <div className="pl-h">Returned</div>
          <RecordList data={call.result} />
        </div>
      )}
    </>
  );
}

export default function AgentSteps({ tools, busy }) {
  if (busy) {
    return (
      <div className="steps" aria-live="polite">
        {PENDING_SEQUENCE.map((label, i) => (
          <div key={label} className={i === 0 ? 'step running' : 'step'}>
            <span className="step-label">{label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (!tools || !tools.length) return null;

  return (
    <div className="steps">
      {tools.map((call, i) => (
        <Step key={`${call.tool}-${i}`} call={call} />
      ))}
    </div>
  );
}